import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Expand, X } from "lucide-react";
import Reveal from "./Reveal";
import { GALLERY, GALLERY_FILTERS } from "./data";

export default function Gallery() {
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);
  const items = filter === "All" ? GALLERY : GALLERY.filter((g) => g.cat === filter);

  return (
    <section id="gallery" data-testid="gallery-section" className="bg-slate-50 py-16 sm:py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-sapphire">Recent work</p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
              Spaces we&rsquo;ve transformed
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {GALLERY_FILTERS.map((f) => (
              <button
                key={f}
                data-testid={f === "All" ? "gallery-filter-all-button" : `gallery-filter-${f.toLowerCase()}-button`}
                onClick={() => setFilter(f)}
                className={`rounded-full px-5 py-2 text-sm font-semibold transition-all duration-300 ${
                  filter === f
                    ? "bg-sapphire text-white shadow-lg shadow-sapphire/25"
                    : "border border-slate-200 bg-white text-slate-600 hover:border-sapphire hover:text-sapphire"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </Reveal>

        <motion.div layout className="mt-12 grid grid-cols-2 gap-4 lg:grid-cols-4 lg:gap-6">
          <AnimatePresence mode="popLayout">
            {items.map((g) => (
              <motion.button
                layout
                key={g.src}
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.92 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                onClick={() => setActive(g)}
                data-testid={`gallery-item-${g.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="img-zoom group relative aspect-[4/5] overflow-hidden rounded-2xl border border-slate-200 bg-white text-left"
              >
                <img src={g.src} alt={g.label} loading="lazy" className="h-full w-full object-cover" />
                <span className="absolute inset-0 bg-gradient-to-t from-sapphire-ink/70 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                <span className="absolute bottom-3 left-3 right-3 flex items-center justify-between text-white opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                  <span>
                    <span className="block text-[10px] font-bold uppercase tracking-[0.2em] text-white/70">
                      {g.cat}
                    </span>
                    <span className="block text-sm font-bold">{g.label}</span>
                  </span>
                  <Expand className="h-4 w-4" />
                </span>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="gallery-spotlight-modal"
            onClick={() => setActive(null)}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/85 p-4 backdrop-blur-sm"
          >
            <motion.figure
              initial={{ scale: 0.9, y: 24 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.92, y: 16 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-3xl"
            >
              <img src={active.src} alt={active.label} className="max-h-[76vh] w-auto rounded-2xl shadow-2xl" />
              <figcaption className="mt-4 flex items-center justify-between">
                <span>
                  <span className="block text-[10px] font-bold uppercase tracking-[0.2em] text-white/60">
                    {active.cat}
                  </span>
                  <span className="block text-lg font-bold text-white">{active.label}</span>
                </span>
                <button
                  data-testid="gallery-modal-close-button"
                  onClick={() => setActive(null)}
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
